export interface MarketSchedule {
  /** 0 = Sunday, 6 = Saturday */
  marketDay: number;
  openHour: number;
  closeHour: number;
}

export const MARKET_SCHEDULE: MarketSchedule = {
  marketDay: 6,
  openHour: 9,
  closeHour: 14,
};

/**
 * The market lives in Troy, NY. All open/closed computations are done in this
 * timezone so the chip is correct for visitors anywhere (and across DST).
 */
export const MARKET_TIMEZONE = 'America/New_York';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatHour(h: number): string {
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12} ${period}`;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

// Module-level formatter — Intl.DateTimeFormat construction is expensive and
// this one is hit on every minute tick.
const marketTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MARKET_TIMEZONE,
  weekday: 'short',
  month: 'numeric',
  hour: 'numeric',
  hourCycle: 'h23',
});

/** Wall-clock weekday, hour, and 1-based month for `date` in the market's timezone. */
function marketWallClock(date: Date): { weekday: number; hour: number; month: number } {
  let weekday = 0;
  let hour = 0;
  let month = 0;
  for (const part of marketTimeFormatter.formatToParts(date)) {
    if (part.type === 'weekday') weekday = WEEKDAY_INDEX[part.value] ?? 0;
    if (part.type === 'hour') hour = Number(part.value);
    if (part.type === 'month') month = Number(part.value);
  }
  return { weekday, hour, month };
}

/**
 * Whether the market session the chip's "Opens …" text points at falls in the
 * outdoor season (May–Oct), computed in the market's timezone. Unlike
 * `isOutdoorSeason`, a post-close market day rolls to next week's session —
 * matching the `Opens Saturday` text shown after closing.
 */
function nextSessionIsOutdoor(now: Date, schedule: MarketSchedule): boolean {
  const { weekday, hour } = marketWallClock(now);
  let daysUntil = (schedule.marketDay - weekday + 7) % 7;
  if (daysUntil === 0 && hour >= schedule.closeHour) daysUntil = 7;
  const { month } = marketWallClock(new Date(now.getTime() + daysUntil * 86_400_000));
  return month >= 5 && month <= 10;
}

/** Whether the market is open at the instant `now` (Troy, NY wall clock). */
export function isOpenNow(
  now: Date = new Date(),
  schedule: MarketSchedule = MARKET_SCHEDULE,
): boolean {
  const { weekday, hour } = marketWallClock(now);
  return weekday === schedule.marketDay && hour >= schedule.openHour && hour < schedule.closeHour;
}

/**
 * Live status label for the top-bar chip:
 * - `Open now · until 2 PM` while the market is running
 * - `Opens today 9 AM` on market-day mornings
 * - `Opens Saturday 9 AM` the rest of the week
 * Indoor-season (Nov–Apr) closed states get an ` · indoors` suffix so
 * visitors aren't sent to the outdoor square off-season.
 */
export function statusLabel(
  now: Date = new Date(),
  schedule: MarketSchedule = MARKET_SCHEDULE,
): string {
  if (isOpenNow(now, schedule)) {
    return `Open now · until ${formatHour(schedule.closeHour)}`;
  }
  const { weekday, hour } = marketWallClock(now);
  const isMarketDayMorning = weekday === schedule.marketDay && hour < schedule.openHour;
  const day = isMarketDayMorning ? 'today' : (DAYS[schedule.marketDay] ?? 'Saturday');
  const opens = `Opens ${day} ${formatHour(schedule.openHour)}`;
  return nextSessionIsOutdoor(now, schedule) ? opens : `${opens} · indoors`;
}

export function scheduleLabel(schedule: MarketSchedule = MARKET_SCHEDULE): string {
  const day = DAYS[schedule.marketDay] ?? 'Saturday';
  return `${day}s · ${formatHour(schedule.openHour)}–${formatHour(schedule.closeHour)}`;
}

/**
 * Returns the date of the next market day. If `now` falls on the market day,
 * returns `now` (today is the next market day). Otherwise advances to the
 * upcoming occurrence of `schedule.marketDay`.
 */
export function nextMarketDay(
  now: Date = new Date(),
  schedule: MarketSchedule = MARKET_SCHEDULE,
): Date {
  const today = now.getDay();
  const daysUntil = (schedule.marketDay - today + 7) % 7;
  const result = new Date(now);
  result.setDate(now.getDate() + daysUntil);
  return result;
}

/**
 * Whether the *upcoming* market day falls in the outdoor season (May–Oct).
 * Uses the next market day rather than today so that mid-week visits before
 * a season-boundary Saturday show the correct location. Resolves issue #26.
 */
export function isOutdoorSeason(
  now: Date = new Date(),
  schedule: MarketSchedule = MARKET_SCHEDULE,
): boolean {
  const month = nextMarketDay(now, schedule).getMonth();
  return month >= 4 && month <= 9;
}
